import React, { useCallback, useEffect, useRef } from 'react';
import { asDraggableContentListItem, useSortableListItem } from '../../../_lib/components/SortableContentList';
import { StatusInfoError } from '../../../_lib/components/StatusInfo';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { ContentHeadline } from '../../../_lib/components/ContentHeadline';
import { PageTypeRole } from '../PageTypeRoles.types';
import Btn, { BtnModifier } from '../../../components/molecules/Btn';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { Icons } from '../../../components/atoms/SVG';
import { ContentParagraph } from '../../../_lib/components/ContentParagraph';

type FavoriteListItemProps = {
  id: string;
  role: PageTypeRole;
};

function PageTypeRoleItem({ role }: FavoriteListItemProps) {
  const { t } = useSonTranslation('PageTypeRoles');

  const { move, nextItemId, prevItemId, error, currentItemIndex } = useSortableListItem(role.id);

  const currentPosition = useRef<number>(currentItemIndex + 1);

  const moveElementUp = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (prevItemId) {
        event.preventDefault();
        event.stopPropagation();

        move(prevItemId);
      }
    },
    [move, prevItemId],
  );

  const moveElementDown = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (nextItemId) {
        event.preventDefault();
        event.stopPropagation();

        move(nextItemId);
      }
    },
    [move, nextItemId],
  );

  useEffect(() => {
    currentPosition.current = currentItemIndex + 1;
  }, [currentItemIndex]);

  const btnUpText = !prevItemId
    ? t('actions.moveUp.disabled')
    // eslint-disable-next-line react-hooks/refs
    : t('actions.moveUp.text', { position: currentPosition.current - 1 });
  const btnDownText = !nextItemId
    ? t('actions.moveDown.disabled')
    // eslint-disable-next-line react-hooks/refs
    : t('actions.moveDown.text', { position: currentPosition.current + 1 });

  return (
    <div className="page-type-roles__draggable-item">
      <div className="page-type-roles__draggable-item-text-container">
        <ContentHeadline title={role.name} hierarchy={4} modifier="top-headline" />
        <ContentParagraph>{role.description}</ContentParagraph>
      </div>
      <div className="page-type-roles__draggable-item-button-container">
        <Btn
          text={btnUpText}
          titleText={btnUpText}
          hiddenText
          modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
          svgSpriteId={Icons.MOVE_UP}
          ariaDisabled={!prevItemId}
          tooltipPlacement={TooltipPlacement.TOP}
          onClick={moveElementUp}
        />
        <Btn
          text={btnDownText}
          titleText={btnDownText}
          hiddenText
          modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
          svgSpriteId={Icons.MOVE_DOWN}
          ariaDisabled={!nextItemId}
          tooltipPlacement={TooltipPlacement.TOP}
          onClick={moveElementDown}
        />
      </div>
      {error && <StatusInfoError message={t('sortErrorResponse')} modifier="padding-top" />}
    </div>
  );
}

export const DraggablePageTypeRoleItem = asDraggableContentListItem(PageTypeRoleItem);
